import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { ArrowBack, Save, Publish, Add, Delete } from '@mui/icons-material';
import { useTrainingManagement } from '../hooks/useTrainingManagement';
import { trainingManagementService } from '../services/trainingManagementService';
import QuizBuilder from '../components/QuizBuilder';
import VideoUploadForm from '../components/VideoUploadForm';

export default function CourseEditorPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { createCourse, updateCourse, publishCourse } = useTrainingManagement();

  const isNewCourse = courseId === 'new';

  const [loading, setLoading] = useState(false); // Start with false, load only if needed
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [difficultyLevel, setDifficultyLevel] = useState<'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED'>('BEGINNER');
  const [estimatedDuration, setEstimatedDuration] = useState(30);
  const [learningObjectives, setLearningObjectives] = useState<string[]>(['']);
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isRecommended, setIsRecommended] = useState(false);
  const [passingScore, setPassingScore] = useState(80);
  const [courseStatus, setCourseStatus] = useState<'DRAFT' | 'PUBLISHED' | 'ARCHIVED'>('DRAFT');

  useEffect(() => {
    if (!isNewCourse && courseId && courseId !== 'new') {
      loadCourse();
    } else {
      // For new courses, ensure loading is false
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, isNewCourse]);

  const loadCourse = async () => {
    if (!courseId || courseId === 'new') return;

    setLoading(true);
    setError(null);
    try {
      const course = await trainingManagementService.getCourse(courseId);
      setTitle(course.title);
      setDescription(course.description);
      setCategory(course.category || '');
      setDifficultyLevel(course.difficultyLevel);
      setEstimatedDuration(course.estimatedDuration);
      setLearningObjectives(course.learningObjectives.length > 0 ? course.learningObjectives : ['']);
      setVideoUrl(course.videoUrl || '');
      setThumbnailUrl(course.thumbnailUrl || '');
      setIsRequired(course.isRequired);
      setIsRecommended(course.isRecommended);
      setPassingScore(course.passingScore);
      setCourseStatus(course.status);
    } catch (err: any) {
      setError(err.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (andPublish: boolean = false) => {
    // Validation
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }
    if (estimatedDuration <= 0) {
      setError('Duration must be greater than 0');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const courseData = {
        title,
        description,
        category: category || undefined,
        difficultyLevel,
        estimatedDuration,
        learningObjectives: learningObjectives.filter(obj => obj.trim()),
        videoUrl: videoUrl || undefined,
        thumbnailUrl: thumbnailUrl || undefined,
        isRequired,
        isRecommended,
        passingScore,
      };

      if (isNewCourse) {
        const newCourse = await createCourse(courseData);
        
        // If andPublish is true, publish the course immediately
        if (andPublish) {
          await publishCourse(newCourse.id);
          alert('Course created and published successfully! Caregivers can now see it.');
        } else {
          alert('Course created successfully!');
        }
        
        navigate(`/admin/training/courses/${newCourse.id}`);
      } else if (courseId) {
        await updateCourse(courseId, courseData);
        alert('Course updated successfully!');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!courseId || isNewCourse) {
      setError('Please save the course first');
      return;
    }

    if (!videoUrl) {
      setError('Please add a video before publishing');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await publishCourse(courseId);
      setCourseStatus('PUBLISHED');
      alert('Course published successfully!');
      navigate('/admin/training');
    } catch (err: any) {
      setError(err.message || 'Failed to publish course');
    } finally {
      setSaving(false);
    }
  };

  const addObjective = () => {
    setLearningObjectives([...learningObjectives, '']);
  };

  const updateObjective = (index: number, value: string) => {
    const updated = [...learningObjectives];
    updated[index] = value;
    setLearningObjectives(updated);
  };

  const removeObjective = (index: number) => {
    setLearningObjectives(learningObjectives.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/training')} sx={{ mb: 2 }}>
          Back to Training Management
        </Button>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" fontWeight="bold">
            {isNewCourse ? 'Create New Course' : 'Edit Course'}
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Save />}
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              Save Draft
            </Button>
            {isNewCourse ? (
              <Button
                variant="contained"
                startIcon={<Publish />}
                onClick={() => handleSave(true)}
                disabled={saving}
              >
                Save & Publish
              </Button>
            ) : courseStatus === 'DRAFT' && (
              <Button
                variant="contained"
                startIcon={<Publish />}
                onClick={handlePublish}
                disabled={saving}
              >
                Publish Course
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Course Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Course Details
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Course Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., Patient Care, Safety, Communication"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Difficulty Level"
              value={difficultyLevel}
              onChange={(e) => setDifficultyLevel(e.target.value as any)}
            >
              <MenuItem value="BEGINNER">Beginner</MenuItem>
              <MenuItem value="INTERMEDIATE">Intermediate</MenuItem>
              <MenuItem value="ADVANCED">Advanced</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Estimated Duration (minutes)"
              value={estimatedDuration}
              onChange={(e) => setEstimatedDuration(parseInt(e.target.value) || 0)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Passing Score (%)"
              value={passingScore}
              onChange={(e) => setPassingScore(parseInt(e.target.value) || 80)}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />
              }
              label="Required Course"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={isRecommended}
                  onChange={(e) => setIsRecommended(e.target.checked)}
                />
              }
              label="Recommended Course"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Learning Objectives */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Learning Objectives
          </Typography>
          <Button startIcon={<Add />} size="small" onClick={addObjective}>
            Add Objective
          </Button>
        </Box>

        {learningObjectives.map((objective, index) => (
          <Box key={index} display="flex" gap={1} mb={2}>
            <TextField
              fullWidth
              label={`Objective ${index + 1}`}
              value={objective}
              onChange={(e) => updateObjective(index, e.target.value)}
              placeholder="What will learners be able to do after this course?"
            />
            {learningObjectives.length > 1 && (
              <IconButton onClick={() => removeObjective(index)} color="error">
                <Delete />
              </IconButton>
            )}
          </Box>
        ))}
      </Paper>

      {/* Video Upload */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Course Video
        </Typography>
        <VideoUploadForm
          currentVideoUrl={videoUrl}
          onVideoUploaded={(url) => setVideoUrl(url)}
        />
      </Paper>

      {/* Quiz Builder */}
      {!isNewCourse && courseId && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Course Assessment
          </Typography>
          <QuizBuilder courseId={courseId} />
        </Paper>
      )}

      {isNewCourse && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Save the course first to add quiz questions
        </Alert>
      )}

      {/* Save Actions */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={() => navigate('/admin/training')}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={() => handleSave(false)}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Course'}
        </Button>
      </Box>
    </Container>
  );
}

